import { FormControl, MenuItem, Select, Tooltip } from '@material-ui/core';
import { Pagination, PaginationItem } from '@mui/material';
import {
    DataGrid,
    gridPageCountSelector,
    gridPageSelector,
    GridToolbarContainer,
    GridToolbarExport,
    useGridApiContext,
    useGridSelector,
} from '@mui/x-data-grid';
import { forwardRef, useImperativeHandle, useState } from 'react';
import axios from 'axios';
import qs from 'qs';

function CustomToolbar(fileName) {
    return (
        <GridToolbarContainer>
            <GridToolbarExport
                csvOptions={{
                    fileName: fileName,
                    utf8WithBom: true,
                }}
            />
        </GridToolbarContainer>
    );
}

function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <div className='grid-table-footer'>
            <Pagination
                color='primary'
                shape='rounded'
                page={page + 1}
                count={pageCount}
                // @ts-expect-error
                renderItem={(props2) => <PaginationItem {...props2} disableRipple />}
                onChange={(e, value) => apiRef.current.setPage(value - 1)}
            />
        </div>
    );
}

function EmptyPagination() {
    return <div className='grid-table-footer' />;
}

export const pagePerCountOptions = [10, 20, 50, 100];

function defaultTooltipRender(params, column) {
    return (
        params.getValue(params.id, column.field) && (
            <Tooltip title={column.renderCell ? column.renderCell(params) : params.getValue(params.id, column.field)}>
                <div className='grid-cell'>{column.renderCell ? column.renderCell(params) : params.getValue(params.id, column.field)}</div>
            </Tooltip>
        )
    );
}

const RefGridTable = forwardRef(({ columns, hideHeader = false, hidePagination = false }, ref) => {
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [onRowDoubleClick, setOnRowDoubleClick] = useState(null);
    const [onRowClick, setOnRowClick] = useState(null);

    useImperativeHandle(ref, () => ({
        handleRows: (rows, totalCount, page, pageSize) => {
            rows && setRows(rows);
            setTotalCount(totalCount);
            setPage(page);
            setPageSize(pageSize);
        },
        handleDoubleClick: (onRowDoubleClick) => {
            setOnRowDoubleClick(onRowDoubleClick);
        },
        handleClick: (onRowClick) => {
            setOnRowClick(onRowClick);
        },
    }));

    const handleChangePageSize = (e) => {
        setPageSize(e.target.value);
    };

    const handleChangePage = (page) => {
        setPage(page);
    };

    return (
        <div className='grid-table'>
            {hideHeader ? (
                <></>
            ) : (
                <div className='grid-table grid-table-head'>
                    <div className='head-text'>총 {totalCount}건</div>
                    <div className='head-select'>
                        <div>Rows per page</div>
                        <FormControl className='grid-table-head-item' variant='outlined' size='small'>
                            <Select value={pageSize} onChange={handleChangePageSize}>
                                {pagePerCountOptions.map((item, index) => (
                                    <MenuItem key={index} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                </div>
            )}
            <div className='grid-table-body'>
                <DataGrid
                    autoHeight
                    hideFooterSelectedRowCount
                    pagination
                    page={page}
                    pageSize={pageSize}
                    paginationMode='client'
                    columns={columns.map((column) =>
                        column.tooltip
                            ? {
                                  ...column,
                                  renderCell: (params) => defaultTooltipRender(params, column),
                              }
                            : column
                    )}
                    rows={rows}
                    rowCount={totalCount}
                    onPageChange={handleChangePage}
                    onRowDoubleClick={onRowDoubleClick}
                    onRowClick={onRowClick}
                    components={
                        hidePagination
                            ? {
                                  Pagination: EmptyPagination,
                              }
                            : {
                                  Pagination: CustomPagination,
                              }
                    }
                    sx={{
                        '.MuiDataGrid-columnSeparator': {
                            display: 'none',
                        },
                        '&.MuiDataGrid-root': {
                            borderLeft: 'none',
                            borderRight: 'none',
                            borderBottom: 'none',
                        },
                    }}
                />
            </div>
        </div>
    );
});

export default RefGridTable;
